import { Controller } from '@hotwired/stimulus';
import application from '~/stimulus_application';

/**
 * Stimulus controller for the sidebar "Download Spec" button.
 *
 * Watches the main_content turbo frame and updates the download link
 * to scope the spec to the currently visible page context.
 *
 * Pages signal their filter via a `data-spec-filter` attribute on their
 * outermost element inside the frame, e.g.:
 *   data-spec-filter="path=%2Fpets&method=get"   (operation page)
 *   data-spec-filter="group=pets"                 (group page)
 *
 * Pages without this attribute (home, schema, ERD) download the full spec.
 */
export default class DownloadSpecController extends Controller {
  static values = { baseUrl: String };

  declare readonly baseUrlValue: string;

  private boundHandleFrameLoad!: (event: Event) => void;

  connect() {
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this);
    document.addEventListener('turbo:frame-load', this.boundHandleFrameLoad);
    this.syncWithCurrentContent();
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.boundHandleFrameLoad);
  }

  private handleFrameLoad(event: Event) {
    const frame = event.target as HTMLElement;
    if (frame.id !== 'main_content') return;
    this.updateLink(frame);
  }

  private syncWithCurrentContent() {
    const frame = document.getElementById('main_content');
    if (frame) this.updateLink(frame);
  }

  private updateLink(frame: HTMLElement) {
    const link = this.element.querySelector<HTMLAnchorElement>('a');
    if (!link) return;

    const filter = frame.querySelector('[data-spec-filter]')?.getAttribute('data-spec-filter');
    if (filter) {
      const url = new URL(this.baseUrlValue, window.location.origin);
      new URLSearchParams(filter).forEach((value, key) => url.searchParams.set(key, value));
      link.href = url.toString();
    } else {
      link.href = this.baseUrlValue;
    }
  }
}

application.register('openapi-ui--sidebar--download-spec', DownloadSpecController);
